/**
 * Row and "select all" checkboxes that stop click propagation on clickable rows.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/** Props for {@link DataTableRowCheckbox}. */
type DataTableRowCheckboxProps = Readonly<{
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
  className?: string;
  /** Stop row/card click propagation when embedded in clickable rows. */
  stopPropagation?: boolean;
}>;

/** Per-row checkbox; optionally stops click from selecting the card/row. */
export function DataTableRowCheckbox({
  checked,
  disabled = false,
  onCheckedChange,
  className,
  stopPropagation = true,
}: DataTableRowCheckboxProps) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      onClick={
        stopPropagation
          ? (event) => {
              event.stopPropagation();
            }
          : undefined
      }
      className={cn('shrink-0', className)}
      aria-label={checked ? 'Désélectionner la ligne' : 'Sélectionner la ligne'}
    />
  );
}

/** Props for {@link DataTableHeadCheckbox} (select-all on the current page). */
type DataTableHeadCheckboxProps = Readonly<{
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onCheckedChange: () => void;
  className?: string;
}>;

/** Header checkbox for the current page, including an indeterminate state. */
export function DataTableHeadCheckbox({
  checked,
  indeterminate = false,
  disabled = false,
  onCheckedChange,
  className,
}: DataTableHeadCheckboxProps) {
  return (
    <Checkbox
      checked={indeterminate ? 'indeterminate' : checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className={cn('shrink-0', className)}
      aria-label="Tout sélectionner sur la page"
    />
  );
}
